import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Power, PowerOff, Search, Package } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const DuesPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const { toast } = useToast();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/money/dues-packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (error) {
      console.error('Error fetching dues packages:', error);
      toast({
        title: "Error",
        description: "Failed to fetch dues packages",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const handleToggleActive = async (packageId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/money/dues-packages/${packageId}/toggle-active`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchPackages();
        toast({
          title: "Success",
          description: `Dues package ${!currentStatus ? 'activated' : 'deactivated'} successfully`
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update package status",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/money/dues-packages/${selectedPackage.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchPackages();
        setIsDeleteModalOpen(false);
        setSelectedPackage(null);
        toast({
          title: "Success",
          description: "Dues package deleted successfully"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete package",
        variant: "destructive"
      });
    }
  };

  const filteredPackages = packages.filter(pkg =>
    pkg.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCount = packages.filter(p => p.active).length;
  const inactiveCount = packages.length - activeCount;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="dues-packages-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dues Package Management</h1>
            <p className="text-gray-600 mt-2">Configure billing categories and types for your properties</p>
          </div>
          <button
            data-testid="add-dues-package-btn"
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all"
          >
            <Plus size={20} />
            Add Dues Package
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200" data-testid="stat-total-packages">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Packages</p>
                <p className="text-3xl font-bold text-blue-900 mt-2">{packages.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                <Package className="text-blue-700" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200" data-testid="stat-active-packages">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Active Packages</p>
                <p className="text-3xl font-bold text-green-900 mt-2">{activeCount}</p>
              </div>
              <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
                <Power className="text-green-700" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200" data-testid="stat-inactive-packages">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Inactive Packages</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{inactiveCount}</p>
              </div>
              <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                <PowerOff className="text-gray-700" size={24} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            data-testid="dues-packages-search"
            type="text"
            placeholder="Search packages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Packages List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredPackages.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center" data-testid="empty-dues-packages">
          <Package className="mx-auto text-gray-300 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Dues Packages Yet</h3>
          <p className="text-gray-600 mb-6">Create your first dues package to get started with billing management</p>
          <button
            data-testid="empty-add-dues-package-btn"
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            <Plus size={20} />
            Add Your First Package
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Billing Type
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Default Amount
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-testid="dues-packages-tbody">
              {filteredPackages.map((pkg) => (
                <tr key={pkg.id} className="hover:bg-gray-50 transition-colors" data-testid={`dues-package-row-${pkg.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{pkg.icon}</span>
                      <div>
                        <p className="font-semibold text-gray-900" data-testid={`dues-package-name-${pkg.id}`}>{pkg.category}</p>
                        {pkg.description && (
                          <p className="text-sm text-gray-500">{pkg.description}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {pkg.billing_type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-semibold text-gray-900">₹{pkg.default_amount.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      data-testid={`dues-package-toggle-${pkg.id}`}
                      onClick={() => handleToggleActive(pkg.id, pkg.active)}
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        pkg.active
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {pkg.active ? <Power size={14} /> : <PowerOff size={14} />}
                      {pkg.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`dues-package-edit-${pkg.id}`}
                        onClick={() => {
                          setSelectedPackage(pkg);
                          setIsEditModalOpen(true);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        data-testid={`dues-package-delete-${pkg.id}`}
                        onClick={() => {
                          setSelectedPackage(pkg);
                          setIsDeleteModalOpen(true);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(isAddModalOpen || isEditModalOpen) && (
        <DuesPackageModal
          isOpen={isAddModalOpen || isEditModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            setIsEditModalOpen(false);
            setSelectedPackage(null);
          }}
          onSuccess={fetchPackages}
          packageData={selectedPackage}
          isEdit={isEditModalOpen}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedPackage(null);
        }}
        onConfirm={handleDelete}
        title="Delete Dues Package"
        message={`Are you sure you want to delete "${selectedPackage?.category}"? This action cannot be undone.`}
      />
    </div>
  );
};

// Dues Package Add/Edit Modal Component
const DuesPackageModal = ({ isOpen, onClose, onSuccess, packageData, isEdit }) => {
  const [formData, setFormData] = useState({
    category: '',
    billing_type: 'Fixed Amount',
    default_amount: 0,
    active: true,
    icon: '💰',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (packageData && isEdit) {
      setFormData({
        category: packageData.category,
        billing_type: packageData.billing_type,
        default_amount: packageData.default_amount,
        active: packageData.active,
        icon: packageData.icon,
        description: packageData.description || ''
      });
    }
  }, [packageData, isEdit]);

  const iconOptions = [
    '💰', '🏠', '💡', '💧', '🔧', '🚗', '📱', '🍽️', '🧹', '🔒'
  ];

  const billingTypes = [
    'Fixed Amount',
    'Variable Amount',
    'Per Occupancy Unit'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const url = isEdit
        ? `${API_URL}/api/money/dues-packages/${packageData.id}`
        : `${API_URL}/api/money/dues-packages`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: `Dues package ${isEdit ? 'updated' : 'created'} successfully`
        });
        onSuccess();
        onClose();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to save package",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save package",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {isEdit ? 'Edit Dues Package' : 'Add New Dues Package'}
          </h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <Plus size={24} className="rotate-45" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Icon Selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Icon
            </label>
            <div className="flex flex-wrap gap-2">
              {iconOptions.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => setFormData({ ...formData, icon })}
                  className={`p-3 text-2xl rounded-lg border-2 transition-all ${
                    formData.icon === icon
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Category Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Category Name *
            </label>
            <input
              data-testid="dues-package-category-input"
              type="text"
              required
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              placeholder="e.g., Rent, Security Deposit, Maintenance"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Billing Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Billing Type *
            </label>
            <select
              data-testid="dues-package-billing-type-select"
              required
              value={formData.billing_type}
              onChange={(e) => setFormData({ ...formData, billing_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {billingTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Default Amount */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Default Amount (₹)
            </label>
            <input
              data-testid="dues-package-amount-input"
              type="number"
              min="0"
              step="0.01"
              value={formData.default_amount}
              onChange={(e) => setFormData({ ...formData, default_amount: parseFloat(e.target.value) || 0 })}
              placeholder="0"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this dues category"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label htmlFor="active" className="text-sm font-medium text-gray-700">
              Active (Enable this dues package)
            </label>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 pt-4">
            <button
              data-testid="dues-package-cancel-btn"
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="dues-package-submit-btn"
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
            >
              {saving ? 'Saving...' : isEdit ? 'Update Package' : 'Create Package'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DuesPackages;
